import { Box, Button, CircularProgress, Stack, Switch, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { GetTriageSettingsPageDataResponse } from '@provision/entity-types';
import {
  Ban,
  CircleDot,
  Clock,
  Gauge,
  Lightbulb,
  RotateCcw,
  Tag,
  Users,
  Wrench,
  Zap,
} from 'lucide-react';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { ExpandableTextArea } from 'src/components/ExpandableTextArea';
import { SelectMenu } from 'src/components/SelectMenu';
import { SelectMenuOption } from 'src/components/SelectMenu/select-menu.types';
import { SettingsSection, SettingsSectionRow } from 'src/components/SettingsSection';
import {
  PartnerSetting,
  useManagePartnerSettingsValue,
  usePartnerSettings,
  useSaveConversationTriage,
  useTriageSettingsPageData,
} from 'src/graphql';
import {
  parseTriageActionConfig,
  TriageActionConfigSnapshot,
} from 'src/graphql/entities/conversation-triage';
import { TriageActionVisibility } from 'src/graphql/entities/conversation-triage-run';
import { useGlobalStore } from 'src/store';
import { useDebounce } from 'src/utils/hooks';
import { isStringBooleanTrue } from 'src/utils/strings';

const useVisibilityOptions = (): SelectMenuOption[] => {
  const theme = useTheme();
  return [
    {
      value: TriageActionVisibility.Hide,
      title: 'Not allowed',
      description: 'Never runs — Triage skips this tool entirely.',
      icon: <Ban />,
      iconColor: theme.palette.custom.OV.sys.error,
    },
    {
      value: TriageActionVisibility.Show,
      title: 'Suggest',
      description: 'Recommends the action and leaves it for you to apply.',
      icon: <Lightbulb />,
      iconColor: theme.palette.custom.OV.sys.onSurfaceVariant,
    },
    {
      value: TriageActionVisibility.AutoApply,
      title: 'Auto-apply',
      description: 'Runs and applies the result automatically.',
      icon: <Zap />,
      iconColor: theme.palette.custom.OV.sys.secondary,
    },
  ];
};

const TOOL_ICONS: Record<string, ReactNode> = {
  change_urgency: <Gauge />,
  change_status: <CircleDot />,
  change_conversation_type: <Tag />,
  change_conversation_assignee_group: <Users />,
  snooze: <Clock />,
};

const INSTRUCTIONS_SAVE_DEBOUNCE_MS = 1000;
const INSTRUCTIONS_CONTROL = 'instructions';
const INSTRUCTIONS_RESET_CONTROL = 'instructions-reset';

const AiPreferencesContent = ({
  partnerId,
  data,
}: {
  partnerId: number | null;
  data: GetTriageSettingsPageDataResponse;
}) => {
  const { template, currentTriage } = data;
  const visibilityOptions = useVisibilityOptions();

  const { data: enabledSettingRecords } = usePartnerSettings({
    partnerId,
    partnerSetting: PartnerSetting.ConversationTriageEnabled,
  });
  const enabledSetting = Array.isArray(enabledSettingRecords)
    ? enabledSettingRecords.find((record) => record.partnerId === partnerId)
    : undefined;
  const { updatePartnerSettingsValue, createPartnerSettingsValue } =
    useManagePartnerSettingsValue(partnerId);

  const { saveConversationTriage } = useSaveConversationTriage(partnerId);

  const enabled =
    enabledSetting?.value !== undefined
      ? isStringBooleanTrue(enabledSetting.value)
      : currentTriage.enabled;
  const [isTogglingEnabled, setIsTogglingEnabled] = useState(false);
  const [instructions, setInstructions] = useState(
    currentTriage.config?.instructions ?? template.instructions,
  );
  const [snapshot, setSnapshot] = useState<TriageActionConfigSnapshot>(() =>
    parseTriageActionConfig(currentTriage.config?.actionConfig),
  );
  const [pendingControls, setPendingControls] = useState<ReadonlySet<string>>(new Set());

  // Latest intended values, read at save time so a serialized save always
  // persists the freshest full config rather than a stale closure snapshot.
  const instructionsRef = useRef(instructions);
  const snapshotRef = useRef(snapshot);
  const savedInstructionsRef = useRef(instructions);
  const triageIdRef = useRef(currentTriage.config?.conversationTriageId ?? null);
  const llmConfigRef = useRef(currentTriage.config?.llmConfig ?? template.llmConfig);
  // Serializes saves so out-of-order completions can't overwrite newer config,
  // and the row id from the first create is reused instead of creating duplicates.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());

  const handleEnabledToggle = useCallback(
    async (checked: boolean) => {
      setIsTogglingEnabled(true);
      try {
        if (enabledSetting?.partnerSettingValueId) {
          await updatePartnerSettingsValue({
            partnerSettingValueId: enabledSetting.partnerSettingValueId,
            value: String(checked),
            partnerSettingId: PartnerSetting.ConversationTriageEnabled,
          });
        } else {
          await createPartnerSettingsValue({
            value: String(checked),
            partnerSettingId: PartnerSetting.ConversationTriageEnabled,
          });
        }
      } finally {
        setIsTogglingEnabled(false);
      }
    },
    [enabledSetting, updatePartnerSettingsValue, createPartnerSettingsValue],
  );

  const enqueueSave = useCallback(
    (control: string) => {
      setPendingControls((prev) => new Set(prev).add(control));
      const run = saveChainRef.current
        .then(async () => {
          const saved = await saveConversationTriage({
            conversationTriageId: triageIdRef.current,
            instructions: instructionsRef.current,
            actionConfig: JSON.stringify(snapshotRef.current),
            llmConfig: llmConfigRef.current,
          });
          if (saved) {
            triageIdRef.current = saved.conversationTriageId;
            llmConfigRef.current = saved.llmConfig;
            savedInstructionsRef.current = saved.instructions;
          }
        })
        .finally(() => {
          setPendingControls((prev) => {
            const next = new Set(prev);
            next.delete(control);
            return next;
          });
        });
      saveChainRef.current = run.catch(() => undefined);
    },
    [saveConversationTriage],
  );

  const saveInstructionsIfChanged = useCallback(
    (control: string) => {
      if (instructionsRef.current !== savedInstructionsRef.current) {
        enqueueSave(control);
      }
    },
    [enqueueSave],
  );

  const debouncedSaveInstructions = useDebounce(
    () => saveInstructionsIfChanged(INSTRUCTIONS_CONTROL),
    INSTRUCTIONS_SAVE_DEBOUNCE_MS,
  );

  const handleInstructionsChange = useCallback(
    (value: string) => {
      setInstructions(value);
      instructionsRef.current = value;
      debouncedSaveInstructions();
    },
    [debouncedSaveInstructions],
  );

  const handleInstructionsBlur = useCallback(() => {
    saveInstructionsIfChanged(INSTRUCTIONS_CONTROL);
  }, [saveInstructionsIfChanged]);

  const handleInstructionsReset = useCallback(() => {
    setInstructions(template.instructions);
    instructionsRef.current = template.instructions;
    saveInstructionsIfChanged(INSTRUCTIONS_RESET_CONTROL);
  }, [template.instructions, saveInstructionsIfChanged]);

  const handleVisibilityChange = useCallback(
    (toolName: string, visibility: string) => {
      const nextSnapshot = { ...snapshotRef.current, [toolName]: { visibility } };
      setSnapshot(nextSnapshot);
      snapshotRef.current = nextSnapshot;
      enqueueSave(toolName);
    },
    [enqueueSave],
  );

  return (
    <Stack spacing={4}>
      <SettingsSection
        title="Triage Intelligence"
        description="Automatically triages new conversations — inferring properties and routing them to the right place."
      >
        <SettingsSectionRow
          title="Enable Triage Intelligence"
          secondary="Let Triage Intelligence read, classify and route new conversations."
          control={
            <Switch
              checked={enabled}
              disabled={isTogglingEnabled}
              onChange={(e) => void handleEnabledToggle(e.target.checked)}
              inputProps={{ 'aria-label': 'Enable Triage Intelligence' }}
            />
          }
        />
      </SettingsSection>

      <Stack
        spacing={4}
        sx={{
          opacity: enabled ? 1 : 0.4,
          pointerEvents: enabled ? 'auto' : 'none',
          transition: (theme) => theme.transitions.create('opacity'),
        }}
        aria-disabled={!enabled}
      >
        <ExpandableTextArea
          title="Agent instructions"
          value={instructions}
          onChange={(e) => handleInstructionsChange(e.target.value)}
          onBlur={handleInstructionsBlur}
          disabled={!enabled}
          placeholder="Tell the agent how to triage conversations for this partner…"
          action={
            <Button
              variant="text"
              color="inherit"
              onClick={handleInstructionsReset}
              disabled={!enabled || pendingControls.has(INSTRUCTIONS_RESET_CONTROL)}
              startIcon={<RotateCcw size={13} />}
              sx={{
                minWidth: 0,
                px: 1.5,
                py: 0.375,
                borderRadius: '0.375rem',
                color: 'custom.OV.sys.onSurfaceVariant',
                fontSize: '0.71875rem',
                fontWeight: 600,
                textTransform: 'none',
                '& .MuiButton-startIcon': { mr: 0.625 },
                '&:hover': {
                  color: 'custom.OV.sys.onBackground',
                  backgroundColor: 'custom.OV.sys.surfaceContainerHigh',
                },
              }}
            >
              Reset to default
            </Button>
          }
        />

        <SettingsSection
          title="Behavior"
          description="Set how far Triage Intelligence can go for each tool call."
        >
          {template.tools.length === 0 ? (
            <Box sx={{ px: 2.25, py: 2 }}>
              <Typography variant="body2" color="custom.OV.sys.onSurfaceVariant">
                No configurable tools are available.
              </Typography>
            </Box>
          ) : (
            template.tools.map((tool) => (
              <Box key={tool.name} data-testid={`triage-tool-row-${tool.name}`}>
                <SettingsSectionRow
                  icon={TOOL_ICONS[tool.name] ?? <Wrench />}
                  title={tool.label}
                  secondary={tool.description}
                  mobileControlLayout="below"
                  control={
                    <SelectMenu
                      label={`${tool.label} permission`}
                      options={visibilityOptions}
                      value={snapshot[tool.name]?.visibility ?? template.defaultVisibility}
                      onChange={(value) => handleVisibilityChange(tool.name, value)}
                      disabled={!enabled || pendingControls.has(tool.name)}
                      sx={{ minWidth: 160 }}
                    />
                  }
                />
              </Box>
            ))
          )}
        </SettingsSection>
      </Stack>
    </Stack>
  );
};

export const AiPreferences = () => {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { data, isLoading, error, refetch } = useTriageSettingsPageData(partnerId);

  if (isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  if (error || !data) {
    return (
      <Stack alignItems="flex-start" spacing={2} py={2}>
        <Typography variant="body2" color="text.secondary">
          Triage Intelligence settings are unavailable right now.
        </Typography>
        <Button variant="outlined" size="small" onClick={() => void refetch()}>
          Retry
        </Button>
      </Stack>
    );
  }

  return <AiPreferencesContent key={partnerId} partnerId={partnerId} data={data} />;
};
